import { useEffect, useRef, useState } from "react";
import { IconCheck, IconFilter, IconPencil } from "@tabler/icons-react";
import { EMAIL_ACTIVITY_FILTER_OPTIONS } from "./helpers";
import type { EmailActivityFilter } from "./types";

/**
 * The Email column's own header filter.
 *
 * Lives in the header rather than the toolbar so the control sits on the
 * data it scopes, and the toolbar keeps its original layout.
 */
export function EmailActivityHeaderFilter({
  value,
  onChange,
}: {
  value: EmailActivityFilter;
  onChange: (next: EmailActivityFilter) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isFiltered = value !== "all";

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const activeLabel = EMAIL_ACTIVITY_FILTER_OPTIONS.find(
    (option) => option.value === value,
  )?.label;

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((previous) => !previous);
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={isFiltered ? `Email: ${activeLabel}` : "Filter by email activity"}
        className={`inline-flex cursor-pointer items-center gap-1 rounded px-1 py-0.5 transition-colors ${
          isFiltered
            ? "text-emerald-700"
            : "text-inherit hover:text-slate-900"
        }`}
      >
        Email
        <IconFilter
          size={12}
          stroke={isFiltered ? 2.4 : 1.8}
          className={isFiltered ? "text-emerald-600" : "text-slate-400"}
          aria-hidden
        />
      </button>

      {open ? (
        <div
          role="listbox"
          className="absolute left-0 top-full z-30 mt-1 w-44 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 text-left shadow-lg"
        >
          {EMAIL_ACTIVITY_FILTER_OPTIONS.map((option) => {
            const selected = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={(event) => {
                  event.stopPropagation();
                  onChange(option.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-xs font-normal normal-case tracking-normal transition-colors ${
                  selected
                    ? "bg-slate-50 font-medium text-slate-900"
                    : "text-slate-600 hover:bg-slate-50"
                }`}
              >
                <span className="flex w-3.5 shrink-0 justify-center">
                  {selected ? (
                    <IconCheck size={13} stroke={2.4} className="text-emerald-600" />
                  ) : null}
                </span>
                {option.value === "unread" ? (
                  <span className="h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                ) : null}
                {option.value === "draft" ? (
                  <IconPencil
                    size={13}
                    stroke={1.9}
                    className="shrink-0 text-emerald-600"
                  />
                ) : null}
                {option.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
